import React from 'react';
import { PrimaryButton, PrimarySelect } from '../../../components';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import {
  getOrder,
  getOrderCancelReasons,
  orderSelector,
  updateOrder,
} from '../../../../store/slices';

export const CancelOrder = ({ close }: { close: () => void }) => {
  const dispatch = useAppDispatch();
  const { cancelReasons, order } = useAppSelector(orderSelector);
  const [submitting, setSubmitting] = React.useState(false);
  const isExternalOrder = order?.slug?.startsWith('EXT_') ?? false;
  const [cancelReason, setCancelReason] = React.useState('');
  const [processRefund, setProcessRefund] = React.useState('no');

  const onComplete = () => {
    if (order) dispatch(getOrder(order.id));
    close();
  };

  const cancelOrder = async () => {
    if (
      !order ||
      submitting ||
      ['DELIVERED', 'CANCELED'].includes(order.status ?? '')
    )
      return;
    setSubmitting(true);
    try {
      await dispatch(
        updateOrder({
          payload: {
            cancelReason,
            status: 'CANCELED',
            ...(!isExternalOrder && { processRefund: processRefund === 'yes' }),
          },
          cb: onComplete,
          id: order.id,
        })
      ).unwrap();
    } catch {
      // The shared request handler displays the server error; keep the reason for retry.
    } finally {
      setSubmitting(false);
    }
  };

  React.useEffect(() => {
    cancelReasons.length === 0 && dispatch(getOrderCancelReasons());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const refundOptions = [
    { id: 'no', reason: 'No' },
    { id: 'yes', reason: 'Yes' },
  ];

  return (
    <div className="bg-white p-6 h-full">
      <p className="bold text-2xl capitalize">Cancel Order</p>
      <p>Select a reason for the cancellation of this order</p>

      <PrimarySelect
        options={cancelReasons}
        onChange={(e) => setCancelReason(e.currentTarget.value)}
        optionLabel="reason"
        optionValue="id"
        className="w-full"
        placeholder="Select a reason"
        value={cancelReason}
      />

      {!isExternalOrder && (
        <div className="mt-4">
          <PrimarySelect
            options={refundOptions}
            onChange={(e) => setProcessRefund(e.currentTarget.value)}
            optionLabel="reason"
            optionValue="id"
            className="w-full"
            placeholder="Select refund option"
            label="Process Refund?"
            value={processRefund}
          />
        </div>
      )}
      {isExternalOrder && (
        <p className="mt-4 text-sm">
          Each payer will receive a full refund to their wallet, including
          delivery and service charges.
        </p>
      )}

      <div className="mt-7 flex justify-end w-full">
        <PrimaryButton
          disabled={
            submitting ||
            !order ||
            ['DELIVERED', 'CANCELED'].includes(order.status ?? '') ||
            cancelReason.length === 0 ||
            cancelReason === 'Select a reason'
          }
          loading={submitting}
          text="Cancel Order"
          onClick={cancelOrder}
        />
      </div>
    </div>
  );
};
